/* eslint-disable @typescript-eslint/no-explicit-any */

export interface IExportCsvOptionsModel<T = any> {
  fileName: string
  fields: (keyof T)[]
  utf8WithBom?: boolean
}

export const generateExportCsvOptions = <T>(options: IExportCsvOptionsModel<T>): IExportCsvOptionsModel<T> => {
  return options
}

export class CsvExportHelper {
  /**
   * Export data to CSV with UTF-8 BOM encoding support
   */
  static exportToCsv<T = any>(data: T[], options: IExportCsvOptionsModel<T>): void {
    try {
      // Extract headers
      const headers = options.fields.map((field) => String(field))

      // Extract rows data
      const rows = data.map((item) =>
        options.fields.map((field) => {
          const value = item[field]
          return this.formatFieldValue(value)
        })
      )

      // Combine headers and rows
      const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n')

      // Create blob with UTF-8 BOM
      let blob: Blob
      if (options.utf8WithBom) {
        // UTF-8 BOM (Byte Order Mark) - helps Excel and other apps recognize UTF-8 encoding
        const BOM = '\uFEFF'
        blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
      } else {
        blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      }

      // Create download link
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `${options.fileName}.csv`)
      link.style.visibility = 'hidden'

      // Trigger download
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting CSV:', error)
      throw new Error('Failed to export CSV file')
    }
  }

  /**
   * Format field value for CSV export
   */
  private static formatFieldValue(value: any): string {
    if (value === null || value === undefined) return ''

    // Handle dates
    if (value instanceof Date) {
      return this.escapeCSVValue(value.toISOString())
    }

    // Handle arrays
    if (Array.isArray(value)) {
      return this.escapeCSVValue(value.join('; '))
    }

    // Handle objects
    if (typeof value === 'object') {
      return this.escapeCSVValue(JSON.stringify(value))
    }

    return this.escapeCSVValue(String(value))
  }

  /**
   * Escape CSV value by wrapping in quotes if needed
   */
  private static escapeCSVValue(value: string): string {
    // If value contains comma, newline, or quote, wrap in quotes and escape internal quotes
    if (value.includes(',') || value.includes('\n') || value.includes('\r') || value.includes('"')) {
      return '"' + value.replace(/"/g, '""') + '"'
    }
    return value
  }
}
